/**
 * Barcode scanning with the phone camera where the browser has the Shape
 * Detection API (Chrome on Android, Edge, Samsung Internet). Everywhere else,
 * and always as a fallback, the code can be typed.
 */
import { BottomSheet, Button, Input } from '@pos/ui';
import { ScanLine } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type DetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike;

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code'];

export function cameraScanSupported(): boolean {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window && !!navigator.mediaDevices?.getUserMedia;
}

type CameraState = 'starting' | 'scanning' | 'denied' | 'unsupported' | 'error';

export function ScannerSheet({
  open,
  onOpenChange,
  onCode,
  title = 'Сканировать штрихкод',
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCode: (code: string) => void;
  title?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<CameraState>('starting');
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) return;
    setTyped('');
    if (!cameraScanSupported()) {
      setState('unsupported');
      return;
    }
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    let last = 0;
    const Detector = (window as unknown as { BarcodeDetector: DetectorCtor }).BarcodeDetector;
    const detector = new Detector({ formats: FORMATS });
    setState('starting');
    void navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then(async (s) => {
        if (stopped) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = s;
        await video.play().catch(() => undefined);
        setState('scanning');
        const tick = async (t: number) => {
          if (stopped) return;
          // ~7 detections a second is plenty and spares the battery.
          if (t - last > 140 && video.readyState >= 2) {
            last = t;
            try {
              const found = await detector.detect(video);
              const code = found[0]?.rawValue?.trim();
              if (code && !stopped) {
                stopped = true;
                navigator.vibrate?.(40);
                onCode(code);
                return;
              }
            } catch {
              // a frame that could not be read; keep going
            }
          }
          raf = requestAnimationFrame((n) => void tick(n));
        };
        raf = requestAnimationFrame((n) => void tick(n));
      })
      .catch((e: unknown) => {
        setState(e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError') ? 'denied' : 'error');
      });
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [open, onCode]);

  const submitTyped = () => {
    const code = typed.trim();
    if (code) onCode(code);
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={title} full>
      <div className="flex h-full flex-col gap-4">
        <div className="relative aspect-[3/4] max-h-[55dvh] w-full overflow-hidden rounded-lg bg-ink">
          <video ref={videoRef} className="size-full object-cover" playsInline muted />
          {state === 'scanning' && (
            <div className="pointer-events-none absolute inset-x-8 top-1/2 h-28 -translate-y-1/2 rounded-lg border-2 border-white/80" aria-hidden>
              <div className="absolute inset-x-3 top-1/2 h-0.5 -translate-y-1/2 bg-danger/80" />
            </div>
          )}
          {state !== 'scanning' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-white">
              <ScanLine className="size-10 opacity-80" />
              <p className="text-md">
                {state === 'starting'
                  ? 'Включаем камеру…'
                  : state === 'denied'
                    ? 'Нет доступа к камере. Разрешите его в настройках браузера или введите код ниже.'
                    : state === 'unsupported'
                      ? 'Этот браузер не умеет читать штрихкоды камерой. Введите код ниже.'
                      : 'Камера недоступна. Введите код ниже.'}
              </p>
            </div>
          )}
        </div>
        <p className="text-center text-sm text-ink-3">Наведите камеру на штрихкод — он считается сам</p>
        <form
          className="mt-auto flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submitTyped();
          }}
        >
          <Input
            size="lg"
            inputMode="numeric"
            autoComplete="off"
            placeholder="Или введите штрихкод"
            aria-label="Штрихкод"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="flex-1"
          />
          <Button type="submit" size="lg" variant="primary" disabled={!typed.trim()}>
            Найти
          </Button>
        </form>
      </div>
    </BottomSheet>
  );
}
